import React, { useState, useEffect } from "react";
import { InternalLayout } from "../Components/InternalLayout";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { getApiUrl } from "../config/api";

export function AdminProdutos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduto, setEditingProduto] = useState(null);
  
  const [formData, setFormData] = useState({
    nome: "",
    tipo: "",
    subtipo: "",
    valor: "",
    ativo: true,
  });

  // Buscar produtos
  useEffect(() => {
    buscarProdutos();
  }, []);

  const buscarProdutos = async () => {
    setLoading(true);
    try {
      const response = await fetch(getApiUrl("/api/checkout/produtos?ativo=all"), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      
      const result = await response.json();
      
      if (result.success) {
        setProdutos(result.data);
      } else {
        setError("Erro ao carregar produtos");
      }
    } catch (error) {
      setError("Erro ao conectar com servidor");
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal para criar/editar
  const abrirDialog = (produto = null) => {
    if (produto) {
      setEditingProduto(produto._id);
      setFormData({
        nome: produto.nome,
        tipo: produto.tipo,
        subtipo: produto.subtipo,
        valor: produto.valor,
        ativo: produto.ativo,
      });
    } else {
      setEditingProduto(null);
      setFormData({
        nome: "",
        tipo: "",
        subtipo: "",
        valor: "",
        ativo: true,
      });
    }
    setOpenDialog(true);
  };

  // Fechar modal
  const fecharDialog = () => {
    setOpenDialog(false);
    setEditingProduto(null);
    setError("");
    setSuccess("");
  };

  // Salvar produto
  const salvarProduto = async () => {
    if (!formData.nome || !formData.tipo || !formData.subtipo || !formData.valor) {
      setError("Todos os campos são obrigatórios");
      return;
    }

    setLoading(true);
    try {
      const url = editingProduto 
        ? getApiUrl(`/api/checkout/produtos?id=${editingProduto}`)
        : getApiUrl("/api/checkout/produtos");
      
      const method = editingProduto ? "PUT" : "POST";
      
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({
          ...formData,
          valor: parseFloat(formData.valor),
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(editingProduto ? "Produto atualizado!" : "Produto criado!");
        buscarProdutos();
        fecharDialog();
      } else {
        setError(result.error || "Erro ao salvar produto");
      }
    } catch (error) {
      setError("Erro ao conectar com servidor");
    } finally {
      setLoading(false);
    }
  };

  // Remover produto
  const removerProduto = async (id) => {
    if (!window.confirm("Tem certeza que deseja remover este produto?")) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(getApiUrl(`/api/checkout/produtos?id=${id}`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      const result = await response.json();

      if (result.success) {
        setSuccess("Produto removido!");
        buscarProdutos();
      } else {
        setError(result.error || "Erro ao remover produto");
      }
    } catch (error) {
      setError("Erro ao conectar com servidor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <InternalLayout>
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Grid container spacing={3} alignItems="center" sx={{ mb: { xs: 2, md: 4 } }}>
          <Grid item>
            <IconButton onClick={() => navigate("/dashboard")} sx={{ color: "primary.main" }}>
              <ArrowBack />
            </IconButton>
          </Grid>
          <Grid item xs>
            <Typography variant="h5" sx={{ color: "primary.main", fontWeight: "bold" }}>
              Gerenciar Produtos
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Cadastre, edite e remova produtos para inscrições
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => abrirDialog()}
              sx={{
                bgcolor: "primary.main",
                "&:hover": { bgcolor: "primary.light" },
                width: { xs: '100%', sm: 'auto' },
                py: { xs: 1.5, md: 1 }
              }}
            >
              Novo Produto
            </Button>
          </Grid>
        </Grid>

        {/* Alertas */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        {/* Tabela de Produtos */}
        <Card>
          <CardContent sx={{ p: { xs: 1, md: 3 } }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 150 }}><strong>Nome</strong></TableCell>
                    <TableCell sx={{ minWidth: 100, display: { xs: 'none', sm: 'table-cell' } }}><strong>Tipo</strong></TableCell>
                    <TableCell sx={{ minWidth: 100, display: { xs: 'none', md: 'table-cell' } }}><strong>Subtipo</strong></TableCell>
                    <TableCell sx={{ minWidth: 80 }}><strong>Valor</strong></TableCell>
                    <TableCell sx={{ minWidth: 90, display: { xs: 'none', sm: 'table-cell' } }}><strong>Status</strong></TableCell>
                    <TableCell sx={{ minWidth: 100 }}><strong>Ações</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {produtos.map((produto) => (
                    <TableRow key={produto._id}>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {produto.nome}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: { sm: 'none' } }}>
                            {produto.tipo} - {produto.subtipo}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {produto.tipo}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {produto.subtipo}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            R$ {produto.valor.toFixed(2)}
                          </Typography>
                          <Box sx={{ display: { sm: 'none' }, mt: 0.5 }}>
                            <Chip 
                              label={produto.ativo ? "Ativo" : "Inativo"} 
                              color={produto.ativo ? "success" : "default"}
                              size="small"
                            />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Chip 
                          label={produto.ativo ? "Ativo" : "Inativo"} 
                          color={produto.ativo ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                          <IconButton 
                            onClick={() => abrirDialog(produto)}
                            sx={{ color: "#1976d2" }}
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            onClick={() => removerProduto(produto._id)}
                            sx={{ color: "#d32f2f" }}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {produtos.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Nenhum produto cadastrado
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Modal Criar/Editar */}
        <Dialog open={openDialog} onClose={fecharDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingProduto ? "Editar Produto" : "Novo Produto"}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nome do Produto"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Tipo"
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  required
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Subtipo"
                  value={formData.subtipo}
                  onChange={(e) => setFormData({ ...formData, subtipo: e.target.value })}
                  required
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Valor (R$)"
                  type="number"
                  value={formData.valor}
                  onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                  inputProps={{ min: 0, step: "0.01" }}
                  required
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.ativo}
                    onChange={(e) => setFormData({ ...formData, ativo: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value={true}>Ativo</MenuItem>
                    <MenuItem value={false}>Inativo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={fecharDialog}>Cancelar</Button>
            <Button 
              onClick={salvarProduto} 
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: "primary.main",
                "&:hover": { bgcolor: "primary.light" },
              }}
            >
              {loading ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </InternalLayout>
  );
}